import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AppLayout from '../components/AppLayout';
import LeaveReportView, { type LeaveReportEntry } from '../components/LeaveReportView';
import OrganisationLeaveHelpSidebar from '../components/OrganisationLeaveHelpSidebar';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
  Checkbox,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { useAuth } from '../contexts/AuthContext';
import { useAgentContext } from '../contexts/AgentContext';
import { usePageAgentContext } from '../hooks/usePageAgentContext';
import { useNavigate } from 'react-router-dom';
import { fiscalYearService } from '../services/fiscalYearService';
import {
  downloadOrganisationLeaveDateRangeReportExcel,
  getOrganisationLeaveDateRangeReportEntries,
} from '../utils/organisationLeaveDateRangeReport';
import SupportingDocumentsTable from '../components/SupportingDocumentsTable';
import { formatDate as formatAppDate } from '../utils/dateFormat';
import { isViewableContentType, openBlobInNewTab } from '../utils/documentViewer';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotLoad, couldNotBook, genericCopy } from '../utils/userFacingCopy';

interface LeaveEntitlementUsageEntry {
  employeeId: string;
  employeeName: string;
  employeeNumber?: number;
  departmentName?: string;
  businessUnitName?: string;
  countryCode?: string;
  leaveTypeCode: string;
  leaveTypeName: string;
  usedDays: number;
  capDays: number;
  periodDescription: string;
  exceededCap: boolean;
  isExempt?: boolean;
  exemptionReason?: string;
}

interface StatutoryAnnualLeaveStatusEntry {
  employeeId: string;
  employeeName: string;
  employeeNumber?: number;
  departmentName?: string;
  businessUnitName?: string;
  periodYear: number;
  annualDaysTaken: number;
  daysStillToTake: number;
  countryCode: string;
  monthsRemaining: number;
  requiredRatePerMonth: number;
  status: 'Green' | 'Amber' | 'Red';
}

interface LeaveRequestEvidenceDto {
  id: string;
  fileName: string;
  description?: string;
  uploadedByName: string;
  uploadedAt: string;
  fileSize: number;
  contentType?: string;
}

interface Department {
  id: string;
  name: string;
}

interface LeaveReportResponse {
  entries: LeaveReportEntry[];
}

interface EmployeeOption {
  id: string;
  fullName: string;
  email: string;
}

interface LeaveTypeDto {
  id: string;
  code: string;
  name: string;
  countsTowardAnnualCap: boolean;
  maxDaysPerYear?: number;
  displayOrder: number;
  isActive: boolean;
}

interface LeaveEntitlementMessageDto {
  accruedToDate: number;
  cycleEntitlement: number;
  takenInCycle: number;
  availableBalance: number;
  requestedDays: number;
  daysWouldBeAdvancedLeave: number;
  fiscalYear: number;
  appliesToThisType: boolean;
  evidenceRequired?: boolean;
  evidenceRequiredReason?: string;
  capMessage?: string;
}

interface LeaveRequestDto {
  id: string;
  requesterId: string;
  leaveTypeId: string;
  fromDate: string;
  toDate: string;
}

export default function OrganisationLeavePage() {
  const { user } = useAuth();
  const { showLegacyHelp } = useAgentContext();
  const navigate = useNavigate();
  const isAdmin = user?.roles?.includes('Administrator') ?? false;

  const [data, setData] = useState<LeaveReportResponse | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [departmentsLoading, setDepartmentsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [periodYear, setPeriodYear] = useState<number>(new Date().getFullYear());
  const [fiscalInitialized, setFiscalInitialized] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [fiscalDefaultDates, setFiscalDefaultDates] = useState<{ from: string; to: string } | null>(null);
  const [departmentFilterId, setDepartmentFilterId] = useState<string>('');
  const [countryFilterCode, setCountryFilterCode] = useState<string>('');
  const [leaveTypeFilterName, setLeaveTypeFilterName] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [chartOpen, setChartOpen] = useState(false);
  const [allLeaveTableOpen, setAllLeaveTableOpen] = useState(false);
  const [statutorySectionOpen, setStatutorySectionOpen] = useState(false);
  const [familySectionOpen, setFamilySectionOpen] = useState(false);
  const [sickSectionOpen, setSickSectionOpen] = useState(false);
  const [familyUsage, setFamilyUsage] = useState<LeaveEntitlementUsageEntry[]>([]);
  const [sickUsage, setSickUsage] = useState<LeaveEntitlementUsageEntry[]>([]);
  const [familyLoading, setFamilyLoading] = useState(false);
  const [sickLoading, setSickLoading] = useState(false);
  const [familySortBy, setFamilySortBy] = useState<'employeeName' | 'departmentName' | 'usedDays' | 'status'>('employeeName');
  const [familySortDir, setFamilySortDir] = useState<'asc' | 'desc'>('asc');
  const [familyPage, setFamilyPage] = useState(0);
  const [familyRowsPerPage, setFamilyRowsPerPage] = useState(10);
  const [sickSortBy, setSickSortBy] = useState<'employeeName' | 'departmentName' | 'usedDays' | 'status'>('employeeName');
  const [sickSortDir, setSickSortDir] = useState<'asc' | 'desc'>('asc');
  const [sickPage, setSickPage] = useState(0);
  const [sickRowsPerPage, setSickRowsPerPage] = useState(10);
  const [statutoryStatus, setStatutoryStatus] = useState<StatutoryAnnualLeaveStatusEntry[]>([]);
  const [statutoryLoading, setStatutoryLoading] = useState(false);
  const [statutorySortBy, setStatutorySortBy] = useState<'employeeName' | 'departmentName' | 'annualDaysTaken' | 'daysStillToTake' | 'status'>('status');
  const [statutorySortDir, setStatutorySortDir] = useState<'asc' | 'desc'>('asc');
  const [statutoryPage, setStatutoryPage] = useState(0);
  const [statutoryRowsPerPage, setStatutoryRowsPerPage] = useState(10);
  const [helpExpanded, setHelpExpanded] = useState(false);
  usePageAgentContext({
    pageId: 'organisation-leave',
    pageTitle: 'Organisation Leave',
    visibleSections: ['organisation-report', 'statutory-status', 'book-on-behalf'],
    suggestedPrompts: [
      'How do I read statutory annual leave risk on this page?',
      'How do I use the country filter in Organisation Leave?',
      'What do the policy usage summaries indicate?',
      'How does book leave on behalf work?',
    ],
  });
  const userDepartmentNames = user?.departmentNames ?? (user?.departmentName ? [user.departmentName] : []);
  const isFinanceDepartment = userDepartmentNames.some((name) => (name ?? '').toLowerCase().includes('finance'));
  const canSeeBceaReports = user?.roles?.some((r) => r === 'Administrator' || r === 'SeniorManager') || isFinanceDepartment;

  const [onBehalfOpen, setOnBehalfOpen] = useState(false);
  const [onBehalfEmployees, setOnBehalfEmployees] = useState<EmployeeOption[]>([]);
  const [onBehalfLeaveTypes, setOnBehalfLeaveTypes] = useState<LeaveTypeDto[]>([]);
  const [onBehalfEmployeeId, setOnBehalfEmployeeId] = useState<string>('');
  const [onBehalfLeaveTypeId, setOnBehalfLeaveTypeId] = useState<string>('');
  const [onBehalfFrom, setOnBehalfFrom] = useState<string>('');
  const [onBehalfTo, setOnBehalfTo] = useState<string>('');
  const [onBehalfHalfDay, setOnBehalfHalfDay] = useState(false);
  const [onBehalfComments, setOnBehalfComments] = useState<string>('');
  const [onBehalfEntitlementMessage, setOnBehalfEntitlementMessage] = useState<LeaveEntitlementMessageDto | null>(null);
  const [onBehalfEntitlementLoading, setOnBehalfEntitlementLoading] = useState(false);
  const [onBehalfSubmitting, setOnBehalfSubmitting] = useState(false);
  const [onBehalfError, setOnBehalfError] = useState<string>('');
  const [onBehalfFiles, setOnBehalfFiles] = useState<File[]>([]);
  const [exportingDateRangeReport, setExportingDateRangeReport] = useState(false);
  const onBehalfFileInputRef = React.useRef<HTMLInputElement>(null);
  const [sickEvidenceOpen, setSickEvidenceOpen] = useState(false);
  const [sickEvidenceRequestId, setSickEvidenceRequestId] = useState<string | null>(null);
  const [sickEvidenceLabel, setSickEvidenceLabel] = useState('');
  const [sickEvidenceDocuments, setSickEvidenceDocuments] = useState<LeaveRequestEvidenceDto[]>([]);
  const [sickEvidenceLoading, setSickEvidenceLoading] = useState(false);

  const employeeCountryById = useMemo(() => {
    const map = new Map<string, string>();
    const add = (employeeId: string | undefined, countryCode: string | undefined) => {
      const code = (countryCode ?? '').trim().toUpperCase();
      if (employeeId && code) map.set(employeeId, code);
    };
    for (const row of statutoryStatus) add(row.employeeId, row.countryCode);
    for (const row of sickUsage) add(row.employeeId, row.countryCode);
    for (const row of familyUsage) add(row.employeeId, row.countryCode);
    return map;
  }, [statutoryStatus, sickUsage, familyUsage]);

  const filterByCountry = useCallback(
    <T extends { employeeId: string }>(list: T[]) => {
      if (!countryFilterCode) return list;
      return list.filter((item) => employeeCountryById.get(item.employeeId) === countryFilterCode);
    },
    [countryFilterCode, employeeCountryById]
  );

  const familyFiltered = useMemo(() => {
    let list = filterByCountry(familyUsage);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (e) =>
          (e.employeeName ?? '').toLowerCase().includes(q) ||
          (e.businessUnitName ?? '').toLowerCase().includes(q)
      );
    }
    if (departmentFilterId) {
      const deptName = departments.find((d) => d.id === departmentFilterId)?.name;
      if (deptName) list = list.filter((e) => (e.departmentName ?? '') === deptName);
    }
    return list;
  }, [familyUsage, searchQuery, departmentFilterId, departments, filterByCountry]);

  const sickFiltered = useMemo(() => {
    let list = filterByCountry(sickUsage);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (e) =>
          (e.employeeName ?? '').toLowerCase().includes(q) ||
          (e.businessUnitName ?? '').toLowerCase().includes(q)
      );
    }
    if (departmentFilterId) {
      const deptName = departments.find((d) => d.id === departmentFilterId)?.name;
      if (deptName) list = list.filter((e) => (e.departmentName ?? '') === deptName);
    }
    return list;
  }, [sickUsage, searchQuery, departmentFilterId, departments, filterByCountry]);

  const statutoryFiltered = useMemo(() => {
    let list = filterByCountry(statutoryStatus);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (e) =>
          (e.employeeName ?? '').toLowerCase().includes(q) ||
          (e.businessUnitName ?? '').toLowerCase().includes(q)
      );
    }
    if (departmentFilterId) {
      const deptName = departments.find((d) => d.id === departmentFilterId)?.name;
      if (deptName) list = list.filter((e) => (e.departmentName ?? '') === deptName);
    }
    return list;
  }, [statutoryStatus, searchQuery, departmentFilterId, departments, filterByCountry]);

  const organisationEntriesWithDays = useMemo(
    () => (data?.entries ?? []).filter((e) => e.daysTaken > 0),
    [data?.entries]
  );

  const filteredOrganisationEntries = useMemo(() => {
    if (!countryFilterCode) return organisationEntriesWithDays;
    return organisationEntriesWithDays.filter((entry) => employeeCountryById.get(entry.employeeId) === countryFilterCode);
  }, [organisationEntriesWithDays, countryFilterCode, employeeCountryById]);

  const leaveReportEntryFilters = useMemo(
    () => ({
      searchQuery,
      leaveTypeFilterName,
      departmentFilterId,
      departments,
      dateFrom,
      dateTo,
    }),
    [searchQuery, leaveTypeFilterName, departmentFilterId, departments, dateFrom, dateTo]
  );

  const dateRangeReportEntries = useMemo(
    () =>
      getOrganisationLeaveDateRangeReportEntries({
        entries: filteredOrganisationEntries,
        ...leaveReportEntryFilters,
      }),
    [filteredOrganisationEntries, leaveReportEntryFilters]
  );

  const handleExportDateRangeReport = () => {
    setExportingDateRangeReport(true);
    try {
      downloadOrganisationLeaveDateRangeReportExcel(dateRangeReportEntries, dateFrom, dateTo);
    } catch {
      setError(genericCopy.leaveReportExport);
    } finally {
      setExportingDateRangeReport(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const c = await fiscalYearService.getCurrent();
      if (cancelled) return;
      setPeriodYear(c.fiscalYear);
      try {
        const range = await fiscalYearService.getFiscalYearDateRange(c.fiscalYear);
        if (cancelled) return;
        setDateFrom(range.from);
        setDateTo(range.to);
        setFiscalDefaultDates(range);
      } catch {
        if (cancelled) return;
        setFiscalDefaultDates(null);
      } finally {
        if (!cancelled) setFiscalInitialized(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDateFromChange = useCallback((v: string) => {
    setDateFrom(v);
    if (!v) return;
    void fiscalYearService.evaluateDate(v).then(({ fiscalYear }) => {
      setPeriodYear((prev) => {
        if (fiscalYear === prev) return prev;
        void fiscalYearService.getFiscalYearDateRange(fiscalYear).then((range) => {
          setDateFrom(range.from);
          setDateTo(range.to);
          setFiscalDefaultDates(range);
        });
        return fiscalYear;
      });
    });
  }, []);

  useEffect(() => {
    setFamilyPage(0);
    setSickPage(0);
    setStatutoryPage(0);
  }, [searchQuery, departmentFilterId, countryFilterCode]);

  const familySorted = useMemo(() => {
    const a = [...familyFiltered];
    a.sort((x, y) => {
      let cmp = 0;
      if (familySortBy === 'employeeName') cmp = (x.employeeName ?? '').localeCompare(y.employeeName ?? '');
      else if (familySortBy === 'departmentName') cmp = (x.departmentName ?? '').localeCompare(y.departmentName ?? '');
      else if (familySortBy === 'usedDays') cmp = x.usedDays - y.usedDays;
      else {
        const sx = x.isExempt ? -1 : (x.exceededCap ? 2 : x.usedDays >= x.capDays ? 1 : 0);
        const sy = y.isExempt ? -1 : (y.exceededCap ? 2 : y.usedDays >= y.capDays ? 1 : 0);
        cmp = sx - sy;
      }
      return familySortDir === 'asc' ? cmp : -cmp;
    });
    return a;
  }, [familyFiltered, familySortBy, familySortDir]);

  const familyPaginated = useMemo(
    () => familySorted.slice(familyPage * familyRowsPerPage, familyPage * familyRowsPerPage + familyRowsPerPage),
    [familySorted, familyPage, familyRowsPerPage]
  );

  const sickSorted = useMemo(() => {
    const a = [...sickFiltered];
    a.sort((x, y) => {
      let cmp = 0;
      if (sickSortBy === 'employeeName') cmp = (x.employeeName ?? '').localeCompare(y.employeeName ?? '');
      else if (sickSortBy === 'departmentName') cmp = (x.departmentName ?? '').localeCompare(y.departmentName ?? '');
      else if (sickSortBy === 'usedDays') cmp = x.usedDays - y.usedDays;
      else {
        const xApproaching = x.capDays > 0 && x.usedDays >= x.capDays * 0.8;
        const yApproaching = y.capDays > 0 && y.usedDays >= y.capDays * 0.8;
        const sx = x.isExempt ? -1 : (x.exceededCap ? 2 : xApproaching ? 1 : 0);
        const sy = y.isExempt ? -1 : (y.exceededCap ? 2 : yApproaching ? 1 : 0);
        cmp = sx - sy;
      }
      return sickSortDir === 'asc' ? cmp : -cmp;
    });
    return a;
  }, [sickFiltered, sickSortBy, sickSortDir]);

  const sickPaginated = useMemo(
    () => sickSorted.slice(sickPage * sickRowsPerPage, sickPage * sickRowsPerPage + sickRowsPerPage),
    [sickSorted, sickPage, sickRowsPerPage]
  );

  const statusOrder = (s: 'Green' | 'Amber' | 'Red') => (s === 'Red' ? 0 : s === 'Amber' ? 1 : 2);
  const statutorySorted = useMemo(() => {
    const a = [...statutoryFiltered];
    a.sort((x, y) => {
      let cmp = 0;
      if (statutorySortBy === 'employeeName') cmp = (x.employeeName ?? '').localeCompare(y.employeeName ?? '');
      else if (statutorySortBy === 'departmentName') cmp = (x.departmentName ?? '').localeCompare(y.departmentName ?? '');
      else if (statutorySortBy === 'annualDaysTaken') cmp = x.annualDaysTaken - y.annualDaysTaken;
      else if (statutorySortBy === 'daysStillToTake') cmp = x.daysStillToTake - y.daysStillToTake;
      else cmp = statusOrder(x.status) - statusOrder(y.status);
      return statutorySortDir === 'asc' ? cmp : -cmp;
    });
    return a;
  }, [statutoryFiltered, statutorySortBy, statutorySortDir]);

  const statutoryPaginated = useMemo(
    () => statutorySorted.slice(statutoryPage * statutoryRowsPerPage, statutoryPage * statutoryRowsPerPage + statutoryRowsPerPage),
    [statutorySorted, statutoryPage, statutoryRowsPerPage]
  );

  const handleFamilySort = (field: typeof familySortBy) => {
    if (familySortBy === field) setFamilySortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setFamilySortBy(field);
      setFamilySortDir('asc');
    }
    setFamilyPage(0);
  };

  const handleSickSort = (field: typeof sickSortBy) => {
    if (sickSortBy === field) setSickSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSickSortBy(field);
      setSickSortDir('asc');
    }
    setSickPage(0);
  };

  const handleStatutorySort = (field: typeof statutorySortBy) => {
    if (statutorySortBy === field) setStatutorySortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setStatutorySortBy(field);
      setStatutorySortDir('asc');
    }
    setStatutoryPage(0);
  };

  const getSortIcon = (active: boolean, direction: 'asc' | 'desc') =>
    !active ? <UnfoldMoreIcon sx={{ fontSize: 16, opacity: 0.5 }} /> : direction === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />;

  type FamilySickSortField = 'employeeName' | 'departmentName' | 'usedDays' | 'status';
  const SortableHeader = ({
    field,
    label,
    currentSort,
    direction,
    onSort,
  }: {
    field: FamilySickSortField;
    label: string;
    currentSort: string;
    direction: 'asc' | 'desc';
    onSort: (f: FamilySickSortField) => void;
  }) => (
    <Box
      component="span"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }}
      onClick={() => onSort(field)}
    >
      <strong>{label}</strong>
      {getSortIcon(currentSort === field, currentSort === field ? direction : 'asc')}
    </Box>
  );

  type StatutorySortField = 'employeeName' | 'departmentName' | 'annualDaysTaken' | 'daysStillToTake' | 'status';
  const StatutorySortableHeader = ({
    field,
    label,
    currentSort,
    direction,
    onSort,
  }: {
    field: StatutorySortField;
    label: string;
    currentSort: string;
    direction: 'asc' | 'desc';
    onSort: (f: StatutorySortField) => void;
  }) => (
    <Box
      component="span"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }}
      onClick={() => onSort(field)}
    >
      <strong>{label}</strong>
      {getSortIcon(currentSort === field, currentSort === field ? direction : 'asc')}
    </Box>
  );

  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const res = await apiService.get<Department[]>(API_ENDPOINTS.REFERENCE_DATA.DEPARTMENTS.BASE);
        setDepartments(res.data ?? []);
      } catch {
        setDepartments([]);
      } finally {
        setDepartmentsLoading(false);
      }
    };
    loadDepartments();
  }, []);

  const load = useCallback(async () => {
    if (!fiscalInitialized) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ periodYear: String(periodYear) });
      if (departmentFilterId) params.set('departmentId', departmentFilterId);
      const res = await apiService.get<LeaveReportResponse>(
        `${API_ENDPOINTS.LEAVE.ORGANISATION_REPORT}?${params.toString()}`
      );
      setData(res.data ?? { entries: [] });
    } catch (err) {
            setError(getApiErrorMessage(err, couldNotLoad('the organisation leave report')));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [periodYear, departmentFilterId, fiscalInitialized]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (onBehalfOpen && isAdmin) {
      apiService.get<EmployeeOption[]>(API_ENDPOINTS.USERS.MANAGERS)
        .then((r) => setOnBehalfEmployees(Array.isArray(r.data) ? r.data : []))
        .catch(() => setOnBehalfEmployees([]));
      apiService.get<LeaveTypeDto[]>(API_ENDPOINTS.LEAVE_REQUESTS.LEAVE_TYPES)
        .then((r) => {
          const list = Array.isArray(r.data) ? r.data : [];
          setOnBehalfLeaveTypes(list);
          setOnBehalfLeaveTypeId((prev) => prev || (list[0]?.id ?? ''));
        })
        .catch(() => setOnBehalfLeaveTypes([]));
    }
  }, [onBehalfOpen, isAdmin]);

  useEffect(() => {
    if (!onBehalfOpen || !onBehalfEmployeeId || !onBehalfLeaveTypeId || !onBehalfFrom || !onBehalfTo) {
      setOnBehalfEntitlementMessage(null);
      return;
    }
    const fromDate = new Date(onBehalfFrom);
    const toDate = new Date(onBehalfTo);
    if (toDate < fromDate) {
      setOnBehalfEntitlementMessage(null);
      return;
    }
    setOnBehalfEntitlementLoading(true);
    apiService.get<LeaveEntitlementMessageDto>(
      API_ENDPOINTS.LEAVE_REQUESTS.ENTITLEMENT_MESSAGE(
        onBehalfLeaveTypeId,
        onBehalfFrom,
        onBehalfTo,
        onBehalfEmployeeId,
        onBehalfHalfDay
      )
    )
      .then((r) => setOnBehalfEntitlementMessage(r.data ?? null))
      .catch(() => setOnBehalfEntitlementMessage(null))
      .finally(() => setOnBehalfEntitlementLoading(false));
  }, [onBehalfOpen, onBehalfEmployeeId, onBehalfLeaveTypeId, onBehalfFrom, onBehalfTo, onBehalfHalfDay]);

  const uploadFilesToRequest = useCallback(async (requestId: string, files: File[], description: string) => {
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      if (description.trim()) formData.append('description', description.trim());
      await apiService.post(API_ENDPOINTS.LEAVE_REQUESTS.EVIDENCE_UPLOAD(requestId), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
  }, []);

  const handleOpenOnBehalf = () => {
    setOnBehalfError('');
    setOnBehalfEmployeeId('');
    setOnBehalfLeaveTypeId(onBehalfLeaveTypes[0]?.id ?? '');
    setOnBehalfFrom('');
    setOnBehalfTo('');
    setOnBehalfHalfDay(false);
    setOnBehalfComments('');
    setOnBehalfFiles([]);
    setOnBehalfEntitlementMessage(null);
    if (onBehalfFileInputRef.current) onBehalfFileInputRef.current.value = '';
    setOnBehalfOpen(true);
  };

  const handleBookLeaveOnBehalf = async () => {
    if (!onBehalfEmployeeId || !onBehalfLeaveTypeId || !onBehalfFrom || !onBehalfTo) return;
    const evidenceRequired = onBehalfEntitlementMessage?.evidenceRequired ?? false;
    if (evidenceRequired && onBehalfFiles.length === 0) {
      setOnBehalfError('Evidence is required for this leave type. Please upload at least one document.');
      return;
    }
    setOnBehalfSubmitting(true);
    setOnBehalfError('');
    try {
      const createRes = await apiService.post<LeaveRequestDto>(API_ENDPOINTS.LEAVE_REQUESTS.BASE, {
        leaveTypeId: onBehalfLeaveTypeId,
        fromDate: onBehalfFrom,
        toDate: onBehalfTo,
        isHalfDay: onBehalfHalfDay,
        comments: onBehalfComments || undefined,
        onBehalfOfEmployeeId: onBehalfEmployeeId,
      });
      const created = createRes.data;
      if (created?.id) {
        if (onBehalfFiles.length > 0) {
          await uploadFilesToRequest(created.id, onBehalfFiles, '');
        }
        await apiService.post(API_ENDPOINTS.LEAVE_REQUESTS.SUBMIT(created.id));
      }
      await load();
      setOnBehalfOpen(false);
    } catch (err) {
            setOnBehalfError(getApiErrorMessage(err, couldNotBook('leave on behalf of this employee')));
    } finally {
      setOnBehalfSubmitting(false);
    }
  };

  const applyFileAccept = '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt,.csv,.eml,.msg';

  useEffect(() => {
    if (!canSeeBceaReports || !fiscalInitialized) return;
    const loadFamily = async () => {
      setFamilyLoading(true);
      try {
        const res = await apiService.get<LeaveEntitlementUsageEntry[]>(API_ENDPOINTS.LEAVE.FAMILY_RESPONSIBILITY_USAGE(periodYear));
        setFamilyUsage(Array.isArray(res.data) ? res.data : []);
      } catch {
        setFamilyUsage([]);
      } finally {
        setFamilyLoading(false);
      }
    };
    loadFamily();
  }, [canSeeBceaReports, periodYear, fiscalInitialized]);

  useEffect(() => {
    if (!canSeeBceaReports || !fiscalInitialized) return;
    const loadSick = async () => {
      setSickLoading(true);
      try {
        const res = await apiService.get<LeaveEntitlementUsageEntry[]>(
          API_ENDPOINTS.LEAVE.SICK_LEAVE_USAGE(dateFrom || undefined, dateTo || undefined)
        );
        setSickUsage(Array.isArray(res.data) ? res.data : []);
      } catch {
        setSickUsage([]);
      } finally {
        setSickLoading(false);
      }
    };
    loadSick();
  }, [canSeeBceaReports, fiscalInitialized, dateFrom, dateTo]);

  useEffect(() => {
    if (!canSeeBceaReports || !fiscalInitialized) return;
    const loadStatutory = async () => {
      setStatutoryLoading(true);
      try {
        const res = await apiService.get<StatutoryAnnualLeaveStatusEntry[]>(API_ENDPOINTS.LEAVE.STATUTORY_ANNUAL_STATUS(periodYear));
        setStatutoryStatus(Array.isArray(res.data) ? res.data : []);
      } catch {
        setStatutoryStatus([]);
      } finally {
        setStatutoryLoading(false);
      }
    };
    loadStatutory();
  }, [canSeeBceaReports, periodYear, fiscalInitialized]);

  const loadSickEvidenceDocuments = useCallback(async (requestId: string) => {
    setSickEvidenceLoading(true);
    try {
      const res = await apiService.get<LeaveRequestEvidenceDto[]>(API_ENDPOINTS.LEAVE_REQUESTS.EVIDENCE(requestId));
      setSickEvidenceDocuments(Array.isArray(res.data) ? res.data : []);
    } catch {
      setSickEvidenceDocuments([]);
    } finally {
      setSickEvidenceLoading(false);
    }
  }, []);

  const handleOpenSickLeaveEvidence = useCallback(
    (row: LeaveReportEntry) => {
      if (!row.leaveRequestId) return;
      setSickEvidenceRequestId(row.leaveRequestId);
      setSickEvidenceLabel(
        `${row.employeeName} — ${formatAppDate(row.fromDate ?? '')}${row.toDate && row.toDate !== row.fromDate ? ` – ${formatAppDate(row.toDate)}` : ''}`
      );
      setSickEvidenceOpen(true);
      void loadSickEvidenceDocuments(row.leaveRequestId);
    },
    [loadSickEvidenceDocuments]
  );

  const handleCloseSickLeaveEvidence = () => {
    setSickEvidenceOpen(false);
    setSickEvidenceRequestId(null);
    setSickEvidenceLabel('');
    setSickEvidenceDocuments([]);
  };

  const handleSickEvidenceView = async (documentId: string, fileName: string, contentType: string) => {
    if (!sickEvidenceRequestId) return;
    try {
      const response = await apiService.get(
        API_ENDPOINTS.LEAVE_REQUESTS.EVIDENCE_DOWNLOAD(sickEvidenceRequestId, documentId),
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: contentType || 'application/octet-stream' });
      if (isViewableContentType(contentType)) {
        openBlobInNewTab(blob, contentType);
      } else {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch {
      setError(genericCopy.sickLeaveNoteOpen);
    }
  };

  const handleSickEvidenceDownload = async (documentId: string, fileName: string) => {
    if (!sickEvidenceRequestId) return;
    try {
      const response = await apiService.get(
        API_ENDPOINTS.LEAVE_REQUESTS.EVIDENCE_DOWNLOAD(sickEvidenceRequestId, documentId),
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError(genericCopy.sickLeaveNoteDownload);
    }
  };

  return (
    <AppLayout>
      <LeaveReportView
        title="Organisation Leave"
        icon={<EventAvailableIcon sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5 }} />}
        belowHeader={
          <Alert severity="info" sx={{ mb: 3 }}>
            Organisation-wide leave for the selected date range and filters. Charts and the table reflect overlapping leave in that window; expand sections below for statutory annual and policy usage views. Use the help button for full detail.
          </Alert>
        }
        onBack={() => navigate(-1)}
        entries={filteredOrganisationEntries}
        loading={loading || !fiscalInitialized}
        error={error}
        onClearError={() => setError('')}
        periodYear={periodYear}
        hidePeriodYearSelect
        dateFilter={
          fiscalDefaultDates
            ? {
                from: dateFrom,
                to: dateTo,
                defaultFrom: fiscalDefaultDates.from,
                defaultTo: fiscalDefaultDates.to,
                onFromChange: handleDateFromChange,
                onToChange: setDateTo,
                onReset: () => {
                  setDateFrom(fiscalDefaultDates.from);
                  setDateTo(fiscalDefaultDates.to);
                },
              }
            : undefined
        }
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        leaveTypeFilterName={leaveTypeFilterName}
        onLeaveTypeFilterNameChange={setLeaveTypeFilterName}
        chartOpen={chartOpen}
        onChartOpenChange={setChartOpen}
        tableSectionOpen={allLeaveTableOpen}
        onTableSectionOpenChange={setAllLeaveTableOpen}
        onViewSickLeaveEvidence={handleOpenSickLeaveEvidence}
        emptyMessage="No leave data for this period."
        tableEmptyMessage="No results match your search or filters."
        showDepartmentFilter
        departments={departments}
        departmentFilterId={departmentFilterId}
        onDepartmentFilterIdChange={setDepartmentFilterId}
        departmentsLoading={departmentsLoading}
        additionalFilterControls={
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="org-country-filter-label">Filter by country</InputLabel>
            <Select
              labelId="org-country-filter-label"
              label="Filter by country"
              value={countryFilterCode}
              onChange={(e) => setCountryFilterCode(e.target.value)}
            >
              <MenuItem value="">All countries</MenuItem>
              <MenuItem value="ZA">South Africa (ZA)</MenuItem>
              <MenuItem value="GB">United Kingdom (GB)</MenuItem>
              <MenuItem value="IN">India (IN)</MenuItem>
            </Select>
          </FormControl>
        }
        actions={
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportDateRangeReport}
              disabled={exportingDateRangeReport || dateRangeReportEntries.length === 0}
            >
              {exportingDateRangeReport ? 'Exporting…' : 'Download leave report'}
            </Button>
            {isAdmin ? (
              <Button variant="outlined" startIcon={<AddIcon />} onClick={handleOpenOnBehalf}>
                Book leave on behalf
              </Button>
            ) : null}
          </Box>
        }
        extraSections={
          canSeeBceaReports ? (
            <>
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': { opacity: 0.8 },
                    }}
                    onClick={() => setStatutorySectionOpen((o) => !o)}
                  >
                    <Typography variant="subtitle1">
                      Statutory annual leave status by country
                    </Typography>
                    <IconButton size="small" aria-label={statutorySectionOpen ? 'Collapse' : 'Expand'}>
                      {statutorySectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                  <Collapse in={statutorySectionOpen}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
                      Leave targets are based on each employee&apos;s country (South Africa: 15 days; India: 15 days; United Kingdom: 28 days).
                      Use the country filter to focus on one country.
                      {' '}Risk increases when there are many days left to take and little time left in the fiscal year.
                    </Typography>
                    {statutoryLoading ? (
                    <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <StatutorySortableHeader
                                  field="employeeName"
                                  label="Employee"
                                  currentSort={statutorySortBy}
                                  direction={statutorySortDir}
                                  onSort={handleStatutorySort}
                                />
                              </TableCell>
                              <TableCell>
                                <StatutorySortableHeader
                                  field="departmentName"
                                  label="Department"
                                  currentSort={statutorySortBy}
                                  direction={statutorySortDir}
                                  onSort={handleStatutorySort}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <StatutorySortableHeader
                                  field="annualDaysTaken"
                                  label="Days taken"
                                  currentSort={statutorySortBy}
                                  direction={statutorySortDir}
                                  onSort={handleStatutorySort}
                                />
                              </TableCell>
                              <TableCell align="right">Target</TableCell>
                              <TableCell>
                                <StatutorySortableHeader
                                  field="status"
                                  label="Status"
                                  currentSort={statutorySortBy}
                                  direction={statutorySortDir}
                                  onSort={handleStatutorySort}
                                />
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {statutoryPaginated.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 4, border: 'none' }}>
                                  <Typography color="text.secondary">No employees or no statutory annual leave data for this period.</Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              statutoryPaginated.map((row) => (
                                <TableRow key={row.employeeId} hover>
                                  <TableCell>{row.employeeName}</TableCell>
                                  <TableCell>{row.departmentName ?? '—'}</TableCell>
                                  <TableCell align="right">{row.annualDaysTaken}</TableCell>
                                  <TableCell align="right">{row.statutoryAnnualDays}</TableCell>
                                  <TableCell>
                                    {row.status === 'Green' && (
                                      <Tooltip title="Employee has met their country-specific statutory annual leave target for this fiscal year.">
                                        <Chip size="small" label="Met" color="success" />
                                      </Tooltip>
                                    )}
                                    {row.status === 'Amber' && (
                                      <Tooltip title="Below country statutory target, but enough time remains to catch up at a reasonable rate. Encourage booking leave.">
                                        <Chip size="small" label="At risk" color="warning" />
                                      </Tooltip>
                                    )}
                                    {row.status === 'Red' && (
                                      <Tooltip title="High risk: little time left in the year and/or very high days-per-month needed to reach country target. Prioritise follow-up.">
                                        <Chip size="small" label="High risk" color="error" />
                                      </Tooltip>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        component="div"
                        count={statutoryFiltered.length}
                        page={statutoryPage}
                        onPageChange={(_e, newPage) => setStatutoryPage(newPage)}
                        rowsPerPage={statutoryRowsPerPage}
                        onRowsPerPageChange={(e) => {
                          setStatutoryRowsPerPage(parseInt(e.target.value, 10));
                          setStatutoryPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                      />
                    </>
                  )}
                  </Collapse>
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': { opacity: 0.8 },
                    }}
                    onClick={() => setFamilySectionOpen((o) => !o)}
                  >
                    <Typography variant="subtitle1">
                      Family responsibility leave usage
                    </Typography>
                    <IconButton size="small" aria-label={familySectionOpen ? 'Collapse' : 'Expand'}>
                      {familySectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                  <Collapse in={familySectionOpen}>
                  {familyLoading ? (
                    <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        UK employees are marked <strong>Exempt</strong> in this table because UK family/dependants leave does not use a fixed annual cap in this model.
                      </Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <SortableHeader
                                  field="employeeName"
                                  label="Employee"
                                  currentSort={familySortBy}
                                  direction={familySortDir}
                                  onSort={handleFamilySort}
                                />
                              </TableCell>
                              <TableCell>
                                <SortableHeader
                                  field="departmentName"
                                  label="Department"
                                  currentSort={familySortBy}
                                  direction={familySortDir}
                                  onSort={handleFamilySort}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <SortableHeader
                                  field="usedDays"
                                  label="Used"
                                  currentSort={familySortBy}
                                  direction={familySortDir}
                                  onSort={handleFamilySort}
                                />
                              </TableCell>
                              <TableCell align="right">Cap</TableCell>
                              <TableCell>
                                <SortableHeader
                                  field="status"
                                  label="Status"
                                  currentSort={familySortBy}
                                  direction={familySortDir}
                                  onSort={handleFamilySort}
                                />
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {familyPaginated.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 4, border: 'none' }}>
                                  <Typography color="text.secondary">No family responsibility leave usage in this period.</Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              familyPaginated.map((row) => (
                                <TableRow key={row.employeeId} hover>
                                  <TableCell>{row.employeeName}</TableCell>
                                  <TableCell>{row.departmentName ?? '—'}</TableCell>
                                  <TableCell align="right">{row.usedDays}</TableCell>
                                  <TableCell align="right">{row.isExempt ? '—' : row.capDays}</TableCell>
                                  <TableCell>
                                    {row.isExempt ? (
                                      <Tooltip title={row.exemptionReason ?? 'Exempt from this cap table.'}>
                                        <Chip size="small" label="Exempt" variant="outlined" />
                                      </Tooltip>
                                    ) : row.exceededCap ? (
                                      <Tooltip title="Policy cap exceeded — follow up if needed.">
                                        <Chip size="small" label="Over cap" color="error" />
                                      </Tooltip>
                                    ) : row.usedDays >= row.capDays ? (
                                      <Tooltip title={`Used full ${row.capDays}-day annual entitlement.`}>
                                        <Chip size="small" label="At cap" variant="outlined" />
                                      </Tooltip>
                                    ) : (
                                      '—'
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        component="div"
                        count={familyFiltered.length}
                        page={familyPage}
                        onPageChange={(_e, newPage) => setFamilyPage(newPage)}
                        rowsPerPage={familyRowsPerPage}
                        onRowsPerPageChange={(e) => {
                          setFamilyRowsPerPage(parseInt(e.target.value, 10));
                          setFamilyPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                      />
                    </>
                  )}
                  </Collapse>
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': { opacity: 0.8 },
                    }}
                    onClick={() => setSickSectionOpen((o) => !o)}
                  >
                    <Typography variant="subtitle1">
                      Sick leave usage
                    </Typography>
                    <IconButton size="small" aria-label={sickSectionOpen ? 'Collapse' : 'Expand'}>
                      {sickSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                  <Collapse in={sickSectionOpen}>
                  {sickLoading ? (
                    <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        UK and India employees are marked <strong>Exempt</strong> — their sick leave follows local policy, not the 30-day limit.
                      </Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <SortableHeader
                                  field="employeeName"
                                  label="Employee"
                                  currentSort={sickSortBy}
                                  direction={sickSortDir}
                                  onSort={handleSickSort}
                                />
                              </TableCell>
                              <TableCell>
                                <SortableHeader
                                  field="departmentName"
                                  label="Department"
                                  currentSort={sickSortBy}
                                  direction={sickSortDir}
                                  onSort={handleSickSort}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <SortableHeader
                                  field="usedDays"
                                  label={dateFrom && dateTo ? 'Days in period' : 'Days taken'}
                                  currentSort={sickSortBy}
                                  direction={sickSortDir}
                                  onSort={handleSickSort}
                                />
                              </TableCell>
                              <TableCell align="right">Cap</TableCell>
                              <TableCell>
                                <SortableHeader
                                  field="status"
                                  label="Status"
                                  currentSort={sickSortBy}
                                  direction={sickSortDir}
                                  onSort={handleSickSort}
                                />
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sickPaginated.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 4, border: 'none' }}>
                                  <Typography color="text.secondary">
                                    {dateFrom && dateTo
                                      ? 'No sick leave recorded for the selected period.'
                                      : 'No sick leave recorded in the current three-year period.'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              sickPaginated.map((row) => (
                                <TableRow key={row.employeeId} hover>
                                  <TableCell>{row.employeeName}</TableCell>
                                  <TableCell>{row.departmentName ?? '—'}</TableCell>
                                  <TableCell align="right">{row.usedDays}</TableCell>
                                  <TableCell align="right">{row.isExempt ? '—' : row.capDays}</TableCell>
                                  <TableCell>
                                    {row.isExempt ? (
                                      <Tooltip title={row.exemptionReason ?? 'Exempt from this cap table.'}>
                                        <Chip size="small" label="Exempt" variant="outlined" />
                                      </Tooltip>
                                    ) : row.exceededCap ? (
                                      <Tooltip title="Policy limit exceeded for this three-year period — follow up if needed.">
                                        <Chip size="small" label="Over cap" color="error" />
                                      </Tooltip>
                                    ) : row.capDays > 0 && row.usedDays >= row.capDays * 0.8 ? (
                                      <Tooltip title={`Approaching the 30-day limit: ${row.usedDays} days used in this three-year period.`}>
                                        <Chip size="small" label="Approaching cap" color="warning" />
                                      </Tooltip>
                                    ) : (
                                      '—'
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        component="div"
                        count={sickFiltered.length}
                        page={sickPage}
                        onPageChange={(_e, newPage) => setSickPage(newPage)}
                        rowsPerPage={sickRowsPerPage}
                        onRowsPerPageChange={(e) => {
                          setSickRowsPerPage(parseInt(e.target.value, 10));
                          setSickPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                      />
                    </>
                  )}
                  </Collapse>
                </CardContent>
              </Card>
            </>
          ) : null
        }
      />
      <Dialog open={onBehalfOpen} onClose={() => setOnBehalfOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Book leave on behalf</DialogTitle>
        <DialogContent>
          {onBehalfError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setOnBehalfError('')}>
              {onBehalfError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth size="small" required>
              <InputLabel>Employee</InputLabel>
              <Select
                value={onBehalfEmployeeId}
                label="Employee"
                onChange={(e) => setOnBehalfEmployeeId(e.target.value)}
              >
                {onBehalfEmployees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>{emp.fullName}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Leave type</InputLabel>
              <Select
                value={onBehalfLeaveTypeId || onBehalfLeaveTypes[0]?.id || ''}
                label="Leave type"
                onChange={(e) => setOnBehalfLeaveTypeId(e.target.value)}
              >
                {onBehalfLeaveTypes.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="From date"
              type="date"
              size="small"
              fullWidth
              value={onBehalfFrom}
              onChange={(e) => setOnBehalfFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ '& input::-webkit-calendar-picker-indicator': { cursor: 'pointer' } }}
            />
            <TextField
              label="To date"
              type="date"
              size="small"
              fullWidth
              value={onBehalfTo}
              onChange={(e) => setOnBehalfTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ '& input::-webkit-calendar-picker-indicator': { cursor: 'pointer' } }}
            />
            {onBehalfFrom && onBehalfTo && onBehalfFrom === onBehalfTo && (
              <FormControl component="fieldset" size="small">
                <Typography variant="subtitle2" component="legend" gutterBottom>Duration</Typography>
                <RadioGroup
                  row
                  value={onBehalfHalfDay ? 'half' : 'full'}
                  onChange={(e) => setOnBehalfHalfDay(e.target.value === 'half')}
                >
                  <FormControlLabel value="full" control={<Radio size="small" />} label="Full day" />
                  <FormControlLabel value="half" control={<Radio size="small" />} label="Half day" />
                </RadioGroup>
              </FormControl>
            )}
            {onBehalfFrom && onBehalfTo && onBehalfFrom !== onBehalfTo && (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={onBehalfHalfDay}
                    onChange={(e) => setOnBehalfHalfDay(e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" component="span" display="block">
                      This range includes a half day
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                      Subtracts 0.5 from the working-day total.
                    </Typography>
                  </Box>
                }
              />
            )}
            <TextField
              label="Comments (optional)"
              size="small"
              fullWidth
              multiline
              rows={2}
              value={onBehalfComments}
              onChange={(e) => setOnBehalfComments(e.target.value)}
            />
            <Box>
              <Typography variant="subtitle2" gutterBottom>Supporting documents (optional for most leave types)</Typography>
              {onBehalfEntitlementMessage?.evidenceRequired && onBehalfEntitlementMessage.evidenceRequiredReason && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  {onBehalfEntitlementMessage.evidenceRequiredReason}
                </Alert>
              )}
              <input
                ref={onBehalfFileInputRef}
                type="file"
                accept={applyFileAccept}
                multiple
                onChange={(e) => {
                  const files = e.target.files;
                  if (files?.length) setOnBehalfFiles((prev) => [...prev, ...Array.from(files)]);
                  e.target.value = '';
                }}
                aria-label="Select supporting documents"
                style={{ display: 'none' }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<AttachFileIcon />}
                onClick={() => onBehalfFileInputRef.current?.click()}
                sx={{ textTransform: 'none', mb: 1 }}
              >
                {onBehalfFiles.length === 0 ? 'Select files' : `Add more files (${onBehalfFiles.length} selected)`}
              </Button>
              {onBehalfFiles.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {onBehalfFiles.map((f, i) => (
                    <Chip
                      key={`${f.name}-${i}`}
                      label={f.name}
                      size="small"
                      onDelete={() => setOnBehalfFiles((prev) => prev.filter((_, idx) => idx !== i))}
                      sx={{ maxWidth: 200 }}
                    />
                  ))}
                </Box>
              )}
            </Box>
            {onBehalfEntitlementLoading && <Typography variant="body2" color="text.secondary">Loading…</Typography>}
            {onBehalfEntitlementMessage && !onBehalfEntitlementLoading && (
              <Alert severity="info" sx={{ mt: 1 }}>
                {onBehalfEntitlementMessage.capMessage ? (
                  <>{onBehalfEntitlementMessage.capMessage}</>
                ) : onBehalfEntitlementMessage.appliesToThisType ? (
                  <>
                    This request is for {onBehalfEntitlementMessage.requestedDays} day{onBehalfEntitlementMessage.requestedDays !== 1 ? 's' : ''}.
                    They have already taken {onBehalfEntitlementMessage.takenInCycle} day{onBehalfEntitlementMessage.takenInCycle !== 1 ? 's' : ''} this year.
                  </>
                ) : (
                  <>This request is for {onBehalfEntitlementMessage.requestedDays} day{onBehalfEntitlementMessage.requestedDays !== 1 ? 's' : ''}.</>
                )}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOnBehalfOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleBookLeaveOnBehalf}
            disabled={
              onBehalfSubmitting ||
              !onBehalfEmployeeId ||
              !onBehalfLeaveTypeId ||
              !onBehalfFrom ||
              !onBehalfTo ||
              (!!onBehalfEntitlementMessage?.evidenceRequired && onBehalfFiles.length === 0)
            }
          >
            {onBehalfSubmitting ? <CircularProgress size={20} /> : 'Book leave (auto-approved)'}
          </Button>
        </DialogActions>
      </Dialog>
      <Drawer anchor="right" open={sickEvidenceOpen} onClose={handleCloseSickLeaveEvidence}>
        <Box sx={{ width: { xs: '100vw', sm: 480 }, p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Sick leave notes
          </Typography>
          {sickEvidenceLabel && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {sickEvidenceLabel}
            </Typography>
          )}
          {sickEvidenceLoading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : sickEvidenceDocuments.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No documents were uploaded for this sick leave request.
            </Typography>
          ) : (
            <SupportingDocumentsTable
              documents={sickEvidenceDocuments.map((d) => ({
                id: d.id,
                fileName: d.fileName,
                description: d.description,
                uploadedByName: d.uploadedByName,
                uploadedAt: d.uploadedAt,
                fileSize: d.fileSize,
                contentType: d.contentType,
              }))}
              onView={handleSickEvidenceView}
              onDownload={handleSickEvidenceDownload}
              onDelete={() => {}}
              formatDate={(date) => formatAppDate(date, { includeTime: true })}
            />
          )}
          <Box sx={{ mt: 3 }}>
            <Button onClick={handleCloseSickLeaveEvidence}>Close</Button>
          </Box>
        </Box>
      </Drawer>
      {showLegacyHelp && (
        <OrganisationLeaveHelpSidebar isExpanded={helpExpanded} onToggle={() => setHelpExpanded(!helpExpanded)} />
      )}
    </AppLayout>
  );
}
